import { useLayoutEffect, useRef } from 'react'

type HeaderProps = {
  onLogoClick?: () => void
  progress?: number // 0-100
  children?: React.ReactNode
  rightContent?: React.ReactNode
}

export function Header({ onLogoClick, progress, children, rightContent }: HeaderProps) {
  const headerRef = useRef<HTMLElement>(null)

  useLayoutEffect(() => {
    const element = headerRef.current
    if (!element) return

    const updateHeaderHeight = () => {
      const height = element.getBoundingClientRect().height
      document.documentElement.style.setProperty('--header-height', `${height}px`)
    }

    updateHeaderHeight()

    const observer = new ResizeObserver(updateHeaderHeight)
    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 bg-[#FAF9F7] border-b border-gray-200 z-40 safe-area-top shadow-[0_8px_24px_rgba(11,37,64,0.08)]"
    >
      <div className="px-4 md:px-6 flex items-stretch justify-between gap-3">
        {/* Left: Logo */}
        <div className="flex-shrink-0 flex items-center py-3 md:py-4 w-[126px] md:w-[170px]">
          <h1
            className={`text-xl md:text-2xl font-bold text-gray-900 whitespace-nowrap ${onLogoClick ? 'cursor-pointer hover:opacity-70 transition-opacity' : ''}`}
            onClick={onLogoClick}
          >
            Biodrops
          </h1>
        </div>

        {/* Center: Stepper or Progress */}
        {children && (
          <div className="hidden md:flex flex-1 min-w-0 justify-center items-stretch overflow-hidden -mb-px">{children}</div>
        )}

        {progress !== undefined && (
          <div className="flex-1 flex items-center py-3 md:py-4">
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0B2540] transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Right: Account link or spacer */}
        <div className="flex-shrink-0 flex items-center justify-end py-3 md:py-4 w-[126px] md:w-[170px]">
          {rightContent}
        </div>
      </div>
    </header>
  )
}
